import React, {useState} from 'react';
import {Popup, Button, Image} from 'antd-mobile';
import {useTranslation} from 'react-i18next';
import {getUserInfo} from '@/common-pages/proxy/proxy.service';
import {IProxyUserInfo} from '@/common-pages/proxy/proxy.type';
import {goWhatsAppChat, toPriceStr} from '@/utils';
import PanelSmallItem from '../components/panel-small-item';
import Tier from '../components/tier';
import {
  NewUserIcon,
  BettingUsersIcon,
  HomeRechargeIcon,
  HomeBettingIcon,
} from '@/common-pages/proxy/svg.variable';
import {defaultHeaderImg} from '@/pages/me/me.variable';
const whatsapp = require('@components/assets/icons/proxy/whatsapp.webp');

export function useUserViewModal() {
  const {t} = useTranslation();
  const [user, setUser] = useState<IProxyUserInfo>();
  const [_loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleOpen = (userId: number) => {
    setLoading(true);
    getUserInfo(userId)
      .then(_user => setUser(_user))
      .finally(() => setLoading(false));
    setVisible(true);
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleContact = () => {
    goWhatsAppChat(user?.contactPhone);
  };

  const content = (
    <div className="flex flex-col bg-[#F3F5F8] rounded-t-lg overflow-hidden">
      <div className="flex flex-row justify-between px-5 pt-5 pb-3 items-center bg-white">
        <div className="font-bold text-sm">{t('agent.user-view.title')}</div>
        <div onClick={handleClose}>
          <svg className="w-6 h-6" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </div>
      </div>
      <div className="flex flex-col p-3">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-x-2">
            <div className="relative w-12 h-[3.75rem]">
              <Image
                src={user?.userAvatar || defaultHeaderImg}
                className="w-12 h-12 rounded-full"
                alt=""
              />
              <div className="absolute bottom-0 left-0 z-10">
                <Tier
                  tier={user?.agentLevel?.toString()}
                  className="w-12 h-5 text-xs"
                />
              </div>
            </div>
            <div className="flex flex-col gap-y-1">
              <span className="font-bold text-sm text-t1">
                {user?.userPhone}
              </span>
              <span className="text-t2 text-xs">{user?.activeDay}</span>
            </div>
          </div>
          {user?.agentLevel === 1 && (
            <div className="flex flex-col items-end">
              <span className="text-t2 text-xs">
                {t('agent.user-view.upline')}
              </span>
              <Button
                className="min-w-0 w-8 h-8 p-0 rounded-full bg-transparent"
                onClick={handleContact}>
                <Image src={whatsapp} className="w-8 h-8 rounded-full" alt="" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-row text-xs gap-x-1 mt-2">
          <span className="text-t3">
            {t('agent.team-report.register-date')}
          </span>
          <span className="text-t3">{user?.registerDate}</span>
        </div>
        <div className="mt-3 px-3 py-2 flex flex-col gap-y-3 bg-white">
          <span className="text-t1 font-bold text-xs">
            {t('agent.user-view.teamdata')}
          </span>
          <div className="flex flex-row justify-between items-center pb-3 border-b border-[#E8EAEE]">
            <PanelSmallItem
              icon={<NewUserIcon className="text-t2 size-4" />}
              title={t('agent.user-view.users')}
              result={(user?.totalUserCount || '-') + ''}
              resultClassName="text-t1"
            />
            <PanelSmallItem
              placement="right"
              icon={<BettingUsersIcon className="text-t2 size-4" />}
              title={t('agent.team-report.recharge-user')}
              result={(user?.rechargeUserCount || '-') + ''}
              resultClassName="text-t1"
            />
          </div>
          <div className="flex flex-row">
            <div className="flex-1 border-r border-[#E8EAEE]">
              <PanelSmallItem
                icon={<HomeRechargeIcon className="text-t2 size-4" />}
                title={t('agent.team-report.recharge')}
                result={
                  user?.rechargeAmount ? toPriceStr(+user.rechargeAmount) : '-'
                }
                resultClassName="text-t1"
              />
            </div>
            <div className="flex-1">
              <PanelSmallItem
                icon={<HomeBettingIcon className="text-t2 size-4" />}
                placement="right"
                title={t('agent.team-report.betting')}
                result={user?.betAmount ? toPriceStr(+user.betAmount) : '-'}
                resultClassName="text-t1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return {
    renderModal: (
      <Popup
        visible={visible}
        onClose={handleClose}
        closeOnMaskClick
        position="bottom"
        bodyStyle={{
          padding: 0,
          borderRadius: '8px 8px 0 0',
        }}>
        {content}
      </Popup>
    ),
    show: handleOpen,
  };
}
